import React from 'react';
import PublicLayout from '../PublicLayout/PublicLayout';
import './PrivacyPage.css';

const LAST_UPDATED = 'May 13, 2026';

function PrivacyPage() {
  return (
    <PublicLayout>
      <div className="legal-page">
        <div className="legal-page__container">

          <div className="legal-page__hero">
            <h1 className="legal-page__title">Privacy Policy</h1>
            <p className="legal-page__meta">Last updated: {LAST_UPDATED}</p>
            <p className="legal-page__intro">
              Remori is built on the premise that your journal is yours. This
              policy explains in plain language what information we collect, why
              we collect it, and how we protect it.
            </p>
          </div>

          <div className="legal-page__body">

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">1. Who we are</h2>
              <p>
                Remori ("<strong>Remori</strong>," "we," "us," or "our") is a
                personal journaling application available at{' '}
                <a href="https://remori-journal.com" className="legal-page__link">
                  remori-journal.com
                </a>{' '}
                and as an iOS app. Remori is operated by devhopeslabs. For any
                privacy-related questions, contact us at{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>
                .
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">2. Information we collect</h2>

              <h3 className="legal-page__subsection-title">Information you provide</h3>
              <ul className="legal-page__list">
                <li>
                  <strong>Account information</strong> — your name and email
                  address when you create an account.
                </li>
                <li>
                  <strong>Journal entries</strong> — the text and mood data you
                  write inside the app. This is the core content of the service.
                </li>
                <li>
                  <strong>Settings and preferences</strong> — notification
                  preferences and other in-app configuration you set.
                </li>
              </ul>

              <h3 className="legal-page__subsection-title">Information collected automatically</h3>
              <ul className="legal-page__list">
                <li>
                  <strong>Authentication tokens</strong> — a JWT stored in your
                  browser's local storage to keep you signed in.
                </li>
                <li>
                  <strong>Basic server logs</strong> — standard web server logs
                  (IP address, request path, timestamp) retained for a maximum
                  of 30 days for security and debugging purposes.
                </li>
              </ul>

              <p className="legal-page__callout">
                We do not use third-party analytics, advertising SDKs, or tracking
                pixels. We do not place advertising cookies on your device.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">3. How we use your information</h2>
              <p>We use the information we collect solely to:</p>
              <ul className="legal-page__list">
                <li>Provide, maintain, and improve the Remori service.</li>
                <li>Authenticate you and keep your account secure.</li>
                <li>
                  Generate the insights and mood trends shown inside the app
                  (processed locally or on our servers to compute statistics —
                  never shared or sold).
                </li>
                <li>Send you optional email exports of your entries when you
                  request them.</li>
                <li>Send service-related communications (e.g., password reset
                  emails, important policy changes).</li>
              </ul>
              <p>
                We will never use your journal content for advertising, machine
                learning model training, or any purpose beyond delivering the
                service to you.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">4. How we store and protect your data</h2>
              <p>
                Your data is stored on Amazon Web Services (AWS) infrastructure
                located in the United States. All data is encrypted in transit
                using TLS 1.2 or higher. Journal entries and account data are
                stored in AWS DynamoDB with access restricted to our application
                layer.
              </p>
              <p>
                We restrict access to your personal information to our own systems
                and do not grant any third party direct access to your data.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">5. Data sharing</h2>
              <p>
                <strong>We do not sell, rent, or trade your personal information.</strong>
              </p>
              <p>
                We may disclose information only in the following limited
                circumstances:
              </p>
              <ul className="legal-page__list">
                <li>
                  <strong>Legal requirements</strong> — if required to comply with
                  applicable law, regulation, legal process, or enforceable
                  governmental request.
                </li>
                <li>
                  <strong>Protection of rights</strong> — to protect the rights,
                  property, or safety of Remori, our users, or the public, where
                  disclosure is necessary.
                </li>
                <li>
                  <strong>Service providers</strong> — AWS, our infrastructure
                  provider, processes data on our behalf under strict data
                  processing terms. No other sub-processors have access to your
                  journal content.
                </li>
              </ul>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">6. Your rights and choices</h2>
              <p>You have the following rights over your data:</p>
              <ul className="legal-page__list">
                <li>
                  <strong>Access and export</strong> — you can request a full
                  export of your journal entries at any time from Settings →
                  Export Entries.
                </li>
                <li>
                  <strong>Deletion</strong> — you can delete all your journal
                  entries from Settings → Clear All Data. To delete your account
                  and all associated data entirely, email{' '}
                  <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                    info@devhopeslabs.com
                  </a>{' '}
                  and we will complete the deletion within 30 days.
                </li>
                <li>
                  <strong>Correction</strong> — you can update your name and email
                  address from your Profile page at any time.
                </li>
              </ul>
              <p>
                If you are in the European Economic Area, United Kingdom, or
                California, you may have additional rights under GDPR or CCPA.
                Contact us at the email above to exercise them.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">7. Data retention</h2>
              <p>
                We retain your account and journal entries for as long as your
                account is active. Server logs are deleted after 30 days. If you
                delete your account, we will remove your personal data within 30
                days, except where we are required by law to retain it longer.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">8. Children's privacy</h2>
              <p>
                Remori is not directed at children under the age of 13. We do not
                knowingly collect personal information from children under 13. If
                you believe we have inadvertently collected such information, please
                contact us and we will delete it promptly.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">9. Changes to this policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we do,
                we will revise the "Last updated" date at the top of this page. For
                material changes, we will notify you by email or via an in-app
                notice before the change takes effect.
              </p>
            </section>

            <section className="legal-page__section">
              <h2 className="legal-page__section-title">10. Contact</h2>
              <p>
                Questions or concerns about this Privacy Policy? Reach us at:{' '}
                <a href="mailto:info@devhopeslabs.com" className="legal-page__link">
                  info@devhopeslabs.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PrivacyPage;
